// app/src/lib/track/index.js — first-party funnel tracking for the app.
//
// Exposed as `window.kanjo.track(event, props)`. Fires one POST /v1/event per
// funnel stage (signup_start, signup_complete, plaid_connect_*, activation) so
// we can measure the product funnel without a third-party analytics vendor.
//
// The anonymous id is the join key across the marketing site and the app. It is
// resolved once, in priority order:
//   1. a `.getkanjo.com` cookie set by the marketing site (kanjo-track.js) —
//      shared automatically with the app subdomain;
//   2. a `?a=<id>` query param (cross-origin hand-off when no shared cookie);
//   3. this property's own localStorage;
//   4. a freshly minted id.
// Whatever we resolve is persisted to localStorage so it survives reloads.
//
// Fire-and-forget: every path is wrapped so a tracking failure never throws
// into the caller or breaks a render.

import { getConfig } from '../config.js';
import { currentClaims } from '../auth/cognitoClient.js';

const STORAGE_KEY = 'kanjo_anon_id';

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}

function mint() {
  try {
    if (globalThis.crypto?.randomUUID) return 'a-' + globalThis.crypto.randomUUID();
  } catch { /* fall through */ }
  // Non-crypto fallback (very old webviews). Uniqueness, not secrecy, matters here.
  return 'a-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

let _anonId = null;

/** Resolve (and cache) the anonymous funnel id. See priority order above. */
export function anonId() {
  if (_anonId) return _anonId;
  let id = '';
  try {
    id = readCookie('kanjo_anon');
    if (!id) {
      const p = new URLSearchParams(location.search).get('a');
      if (p) id = p;
    }
    if (!id) id = localStorage.getItem(STORAGE_KEY) || '';
    if (!id) id = mint();
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    id = id || mint();
  }
  _anonId = id;
  return id;
}

/**
 * Record a funnel event. Never awaited by callers — resolves the current
 * Cognito sub (if signed in) and beacons the row.
 * @param {string} event  one of the server-allowlisted funnel event names
 * @param {object} [props] small, PII-free key/values
 */
export async function track(event, props) {
  try {
    if (!event) return;
    let base = '';
    try { base = getConfig().apiBaseUrl; } catch { /* not configured yet */ }
    if (!base) return;

    let userId = '';
    try { userId = (await currentClaims())?.sub || ''; } catch { /* anonymous */ }

    const body = JSON.stringify({
      a: anonId(),
      e: event,
      u: userId,
      p: location.pathname,
      props: props || undefined,
    });
    const url = base.replace(/\/$/, '') + '/v1/event';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body, keepalive: true, mode: 'cors' });
    }
  } catch { /* tracking must never break the app */ }
}
